import { Fragment, ReactNode, useEffect, useState } from "react";

import useBidStore from "@/stores/Bid";

import { GetBidListRequest, OrderBy, SortBy, ValueType } from "@/types/bid";
import { SearchConditionItem } from "@/types/setting";

import {
  BidNoticeSuggestSearchClientList,
  DateRangeList,
  sortList,
} from "@/consts/Bid";

import { formattedOnlyDate, isNotEmpty, subtractDays } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import CommonSelect from "@/components/Common/Select";
import CommonButton from "@/components/Common/Button";
import DialogKeywordGroup from "@/components/DialogKeywordGroup";
import KeywordSearchItem from "@/components/KeywordSearchItem";
import KeywordExcludeItem from "@/components/KeywordExcludeItem";
import CommonImg from "@/components/Common/Img";
import CommonCheckbox from "@/components/Common/Checkbox";
import CommonInput from "@/components/Common/Input";
import { DateRangePicker } from "@/components/DateRangePicker";
import BidLiveTextSearch from "@/components/BidLiveTextSearch";
import CommonPagination from "@/components/Common/Pagination";
import CommonRadio from "@/components/Common/Radio";

import ChevronUpIcon from "@/assets/images/chevron-icon-up.png";
import ChevronDownIcon from "@/assets/images/chevron-icon-down.png";
import { KeywordSearch } from "@/types/search";

type Props = {
  onSearch: (toastFinish: boolean, newFilter?: GetBidListRequest) => void;
  children: ReactNode;
  totalItems?: number;
  searchConditionList: SearchConditionItem[] | [];
  onChangeCondition: (searchId: string) => void;
  onSaveSearchCondition: () => void;
  onRefreshSearchCondition: () => void;
};
export default function BidPlanSearch({
  onSearch,
  children,
  totalItems,
  searchConditionList,
  onChangeCondition,
  onSaveSearchCondition,
  onRefreshSearchCondition,
}: Props) {
  const storedFilter = useBidStore((state) => state.filter);
  const setStoredFilter = useBidStore((state) => state.setFilter);

  const [open, setOpen] = useState<boolean>(false);
  const [openExplanation, setOpenExplanation] = useState<boolean>(false);

  const getExcludeExplanation = (type: string, keywordList: string[]) => {
    if (keywordList.length > 0) {
      const isOver3 = keywordList.length > 3;
      const input_strings = keywordList
        .slice(0, 3)
        .map((string, stringIndex) => (
          <span key={stringIndex} className="text-pink">
            {string}
          </span>
        ));
      return (
        <Fragment>
          {`${type}에서`}
          &nbsp;
          {input_strings.map((el, elIndex) => (
            <span key={elIndex}>
              {el}
              {elIndex < input_strings.length - 1 && <>,&nbsp;</>}
            </span>
          ))}
          &nbsp;
          {isOver3 && <>등</>}을 제외하고,&nbsp;
        </Fragment>
      );
    }
  };

  const handleChangeFilter = (props: any) => {
    const newSettings = { ...storedFilter.settings, ...props };
    const newFilter = {
      ...storedFilter,
      settings: newSettings,
    };
    setStoredFilter(newFilter);
    if (Object.keys(props).includes("page")) {
      onSearch(false, newFilter);
    }
  };

  const handleClickSearch = () => {
    const newFilter = {
      ...storedFilter,
      settings: { ...storedFilter.settings, page: 1 },
    };
    setStoredFilter(newFilter);
    onSearch(true, newFilter);
  };

  const handleSelectDateRangePicker = (e: any) => {
    const newFrom = e.from;
    let newTo = e.to;

    handleChangeFilter({
      min_date: formattedOnlyDate(newFrom),
      max_date: formattedOnlyDate(newTo),
    });
  };

  const onLoad = async () => {};

  useEffect(() => {
    onLoad();
  }, []);

  return (
    storedFilter && (
      <div className="flex flex-col">
        <div className="w-full min-w-[1100px] bg-white rounded-[12px] p-[20px] my-[20px] flex flex-col gap-[12px] shadow-[0_3.5px_5.5px_0_rgba(0,0,0,0.05)]">
          <div className="flex justify-between">
            <div className="flex gap-[8px] items-center">
              <span className="w-[64px] text-gray-B text-xs">검색 조건</span>
              <span />
              <CommonSelect
                defaultValue={
                  searchConditionList.length > 0
                    ? (searchConditionList.find(
                        (condition) =>
                          condition.search_id === storedFilter.search_id
                      )?.search_id ?? "")
                    : ""
                }
                className="min-w-[156px]"
                optionList={searchConditionList.map((condition) => ({
                  label: condition.search_name,
                  value: condition.search_id,
                }))}
                onChange={(value) => {
                  onChangeCondition(value);
                }}
                useAll={false}
              />
              <CommonButton
                label="검색 조건 저장"
                onClick={onSaveSearchCondition}
              />
              <DialogKeywordGroup onRefresh={onRefreshSearchCondition} />
            </div>
          </div>
          {storedFilter.settings.keyword_sets.map(
            (keyword: KeywordSearch, index: number) => {
              return (
                index <
                  (open ? storedFilter.settings.keyword_sets.length : 2) && (
                  <KeywordSearchItem
                    key={index}
                    data={keyword}
                    setData={(newData) => {
                      handleChangeFilter({
                        keyword_sets: [
                          ...storedFilter.settings.keyword_sets.toSpliced(
                            index,
                            1,
                            newData
                          ),
                        ],
                      });
                    }}
                  />
                )
              );
            }
          )}
          {open && (
            <>
              <KeywordExcludeItem
                label={"제목 제외 키워드"}
                data={storedFilter.settings.title_exclude_strings}
                setData={(newData) =>
                  handleChangeFilter({
                    title_exclude_strings: newData,
                  })
                }
              />
              <div className="flex gap-[8px] items-center">
                <div className="min-w-[100px] text-[12px] text-gray-B font-bold">
                  사업 금액
                </div>
                <div className="flex items-center gap-[4px]">
                  <CommonInput
                    type="number"
                    className="min-w-auto"
                    disabled={storedFilter.settings.budget_no_limit}
                    defaultValue={storedFilter.settings.min_budget}
                    onChange={(value: number) => {
                      handleChangeFilter({ min_budget: value });
                    }}
                    onBlur={(value: number) => {
                      if (value > storedFilter.settings.max_budget) {
                        showToast({
                          type: "warning",
                          message: "최소 금액은 최대 금액보다 클 수 없습니다.",
                        });
                        const newMinBudget =
                          storedFilter.settings.max_budget - 1 < 0
                            ? 0
                            : storedFilter.settings.max_budget - 1;
                        handleChangeFilter({
                          min_budget: newMinBudget,
                        });
                      }
                    }}
                  />
                  <span className="text-[10px] font-bold text-gray-B">~</span>
                  <CommonInput
                    type="number"
                    className="min-w-auto"
                    disabled={storedFilter.settings.budget_no_limit}
                    defaultValue={storedFilter.settings.max_budget}
                    onChange={(value: number) => {
                      handleChangeFilter({ max_budget: value });
                    }}
                    onBlur={(value: number) => {
                      if (value < storedFilter.settings.min_budget) {
                        showToast({
                          type: "warning",
                          message:
                            "최대 금액은 최소 금액보다 작을 수 없습니다.",
                        });
                        handleChangeFilter({
                          max_budget: storedFilter.settings.min_budget + 1,
                        });
                      }
                    }}
                  />
                  <CommonCheckbox
                    size="sm"
                    label="금액 제한 없음"
                    defaultValue={storedFilter.settings.budget_no_limit}
                    onChange={(value: boolean) => {
                      handleChangeFilter({ budget_no_limit: value });
                    }}
                  />
                </div>
              </div>

              <div className="flex gap-[8px] items-center">
                <div className="min-w-[100px] text-[12px] text-gray-B font-bold">
                  공고일
                </div>
                <div className="flex flex-col gap-[8px]">
                  <div className="flex items-center gap-[4px]">
                    <DateRangePicker
                      defaultValue={{
                        from: new Date(storedFilter.settings.min_date),
                        to: new Date(storedFilter.settings.max_date),
                      }}
                      disabled={
                        storedFilter.settings.date_range_option_index !== "0"
                      }
                      onSelect={handleSelectDateRangePicker}
                    />
                    <CommonCheckbox
                      size="sm"
                      label="과거 연도 발주 계획 포함"
                      defaultValue={storedFilter.settings.include_finished}
                      onChange={(value) => {
                        handleChangeFilter({ include_finished: value });
                      }}
                    />
                  </div>
                  <div className="flex gap-[14px]">
                    {DateRangeList.map((date, index) => (
                      <CommonRadio
                        key={index}
                        label={date.label}
                        name="date"
                        defaultValue={date.value}
                        checked={
                          date.value ===
                          storedFilter.settings.date_range_option_index.toString()
                        }
                        onChange={(value) => {
                          const maxDate = new Date();
                          if (value !== "0" && value !== "999") {
                            handleChangeFilter({
                              min_date: formattedOnlyDate(
                                subtractDays(maxDate, Number(value)).toString()
                              ),
                              max_date: formattedOnlyDate(maxDate.toString()),
                              date_max_range: false,
                              date_range_option_index: value,
                            });
                          } else if (value === "999") {
                            handleChangeFilter({
                              min_date: "2023-01-01",
                              date_max_range: true,
                              date_range_option_index: value,
                            });
                          } else {
                            handleChangeFilter({
                              date_range_option_index: value,
                            });
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-[8px] items-center">
                <div className="min-w-[100px] text-[12px] text-gray-B font-bold">
                  정렬 기준
                </div>
                <div className="flex items-center gap-[4px]">
                  <CommonSelect
                    className="min-w-[130px]"
                    defaultValue={`${storedFilter.settings.sort_by}|${storedFilter.settings.order}`}
                    optionList={sortList}
                    useAll={false}
                    onChange={(value) => {
                      const [sortValue, orderValue] = value.split("|");
                      handleChangeFilter({
                        sort_by: sortValue as SortBy,
                        order: orderValue as OrderBy,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="flex gap-[8px] items-center">
                <div className="min-w-[100px] text-[12px] text-gray-B font-bold">
                  수요 기관
                </div>
                <div className="flex items-center gap-[4px]">
                  <BidLiveTextSearch
                    placeholder="수요 기관을 입력하세요"
                    method={BidNoticeSuggestSearchClientList}
                    itemKey="client"
                    defaultValue={storedFilter.settings.dminst_filter}
                    onChange={(valueList: ValueType[]) => {
                      handleChangeFilter({ dminst_filter: valueList });
                    }}
                    badgeColor="bg-yellow"
                  />
                </div>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <div
              className="text-gray-B text-xs min-w-[300px] flex justify-center items-center cursor-pointer"
              onClick={() => setOpen((prev) => !prev)}
            >
              추가 검색 조건 목록 {open ? "접기" : "열기"}
              <CommonImg
                src={open ? ChevronUpIcon : ChevronDownIcon}
                alt="Chevron"
                className="size-[16px]"
              />
            </div>
            <CommonButton label={"검색하기"} onClick={handleClickSearch} />
          </div>
        </div>
        <div className="w-full min-w-[1100px] flex justify-center items-top pb-[20px]">
          {openExplanation ? (
            <div className="w-full flex justify-center flex-wrap font-semibold">
              {storedFilter.settings.keyword_sets.map(
                (keyword: KeywordSearch, keywordIndex: number) => {
                  if (keyword.input_strings.length > 0) {
                    const isOver3 = keyword.input_strings.length > 3;
                    const input_strings = keyword.input_strings
                      .slice(0, 3)
                      .map((string, stringIndex) => (
                        <span key={stringIndex} className="text-dark-red">
                          {string}
                        </span>
                      ));
                    return (
                      <Fragment key={keywordIndex}>
                        {`${
                          keyword.title_or_doc === "TITLE"
                            ? "공고 제목"
                            : "첨부파일 본문"
                        }에서`}
                        &nbsp;
                        {input_strings.map((el, elIndex) => (
                          <Fragment key={elIndex}>
                            {el}
                            {elIndex < input_strings.length - 1 && (
                              <>&nbsp;{keyword.or_and}&nbsp;</>
                            )}
                          </Fragment>
                        ))}
                        &nbsp;
                        {isOver3 && <>등</>}을 포함하고,&nbsp;
                      </Fragment>
                    );
                  }
                }
              )}
              {getExcludeExplanation(
                "제목",
                storedFilter.settings.title_exclude_strings
              )}
              {getExcludeExplanation(
                "첨부 파일 본문",
                storedFilter.settings.exclude_strings
              )}
              사업구분은&nbsp;
              <span className="text-dark-red">전체</span>
              &nbsp;에 해당하는 공고를 찾습니다.
            </div>
          ) : (
            <div className="w-full"></div>
          )}
          <div className="min-w-max items-end">
            <CommonCheckbox
              label="설명 보기"
              defaultValue={openExplanation}
              onChange={(value) => setOpenExplanation(value)}
            />
          </div>
        </div>
        <div className="flex gap-[37px]">
          <div className="flex flex-col gap-[16px] w-full min-w-[750px]">
            {children}
            {isNotEmpty(totalItems) && (
              <CommonPagination
                itemsPerPage={storedFilter.settings.page_size}
                totalItems={totalItems ?? 0}
                currentPage={storedFilter.settings.page}
                onPageChange={(page) => {
                  handleChangeFilter({ page: page });
                }}
              />
            )}
          </div>
        </div>
      </div>
    )
  );
}
